import { Link } from "react-router-dom";
import { Mountain, Mail, Phone, MapPin } from "lucide-react";
import { useT } from "@/i18n/TranslationContext";

const Footer = () => {
  const { t, lang } = useT();
  const base = `/${lang}`;
  const year = new Date().getFullYear();

  return (
    <footer className="bg-[#0b2545] text-white/85 mt-24">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16 grid gap-12 lg:grid-cols-12">
        <div className="lg:col-span-4">
          <div className="flex items-center gap-2.5">
            <span className="relative inline-flex items-center justify-center w-10 h-10 rounded-lg bg-white/10 text-white">
              <Mountain className="w-5 h-5" />
              <span className="absolute -bottom-1 -right-1 w-2.5 h-2.5 rounded-full bg-[#c46a3d] ring-2 ring-[#0b2545]" />
            </span>
            <span className="font-extrabold text-white text-lg tracking-tight">Ecominarg</span>
          </div>
          <p className="mt-4 text-sm leading-relaxed text-white/75 max-w-sm">
            {t("footer.tagline")}
          </p>
          <p className="mt-6 text-xs leading-relaxed text-white/55">
            {t("footer.disclaimer")}
          </p>
        </div>

        <div className="lg:col-span-2">
          <h4 className="text-white font-semibold text-sm uppercase tracking-widest mb-4">
            {t("footer.navigation")}
          </h4>
          <ul className="space-y-2.5 text-sm">
            <li>
              <a href={`${base}#sectors`} className="hover:text-[#e9c46a] transition">
                {t("nav.sectors")}
              </a>
            </li>
            <li>
              <a href={`${base}#process`} className="hover:text-[#e9c46a] transition">
                {t("nav.process")}
              </a>
            </li>
            <li>
              <a href={`${base}#transparency`} className="hover:text-[#e9c46a] transition">
                {t("nav.transparency")}
              </a>
            </li>
            <li>
              <a href={`${base}#contact`} className="hover:text-[#e9c46a] transition">
                {t("nav.contact")}
              </a>
            </li>
          </ul>
        </div>

        <div className="lg:col-span-2">
          <h4 className="text-white font-semibold text-sm uppercase tracking-widest mb-4">
            {t("footer.legal")}
          </h4>
          <ul className="space-y-2.5 text-sm">
            <li>
              <Link to={`${base}/privacy-policy`} className="hover:text-[#e9c46a] transition" data-testid="footer-privacy">
                {t("footer.privacy")}
              </Link>
            </li>
            <li>
              <Link to={`${base}/cookie-policy`} className="hover:text-[#e9c46a] transition" data-testid="footer-cookies">
                {t("footer.cookies")}
              </Link>
            </li>
            <li>
              <Link to={`${base}/terms-conditions`} className="hover:text-[#e9c46a] transition" data-testid="footer-terms">
                {t("footer.terms")}
              </Link>
            </li>
          </ul>
        </div>

        <div className="lg:col-span-4">
          <h4 className="text-white font-semibold text-sm uppercase tracking-widest mb-4">
            {t("footer.contact_title")}
          </h4>
          <div className="text-sm space-y-2.5 text-white/80">
            <p className="font-semibold text-white">{t("footer.company")}</p>
            <p className="flex items-start gap-2">
              <MapPin className="w-4 h-4 mt-0.5 shrink-0 text-[#c46a3d]" />
              <span>{t("footer.address")}</span>
            </p>
            <p className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-[#c46a3d]" />
              <a href={`mailto:${t("footer.email")}`} className="hover:text-[#e9c46a]">
                {t("footer.email")}
              </a>
            </p>
            <p className="flex items-center gap-2">
              <Phone className="w-4 h-4 text-[#c46a3d]" />
              <a href={`tel:${t("footer.phone").replace(/\s/g, "")}`} className="hover:text-[#e9c46a]">
                {t("footer.phone")}
              </a>
            </p>
          </div>

          <div className="mt-6 pt-6 border-t border-white/10 text-xs text-white/60 space-y-1.5">
            <p className="font-semibold text-white/80 uppercase tracking-widest text-[10px]">
              {t("footer.legal_details_title")}
            </p>
            <p>{t("footer.cuit")}</p>
            <p>{t("footer.igj")}</p>
            <p>{t("footer.iibb")}</p>
            <p>{t("footer.license")}</p>
            <p>{t("footer.jurisdiction")}</p>
            <p>{t("footer.incorporation")}</p>
          </div>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-6 flex flex-col md:flex-row items-center justify-between gap-2 text-xs text-white/55">
          <p>
            © {year} Ecominarg Research &amp; Analytics S.A.S. — {t("footer.rights")}
          </p>
          <p>ecominarg.com · CABA, Argentina</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
